import React, { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import Lenis from 'lenis';
import { Star, ArrowRight, Play, Shield, CheckCircle2 } from 'lucide-react';

const CALENDLY_URL = 'https://calendly.com/rhomy-startsolutionsenterprise/consultoria-de-credito';
const VIDEO_URL = '/vsl.mp4';
const VIDEO_POSTER = '/vsl-poster.jpg';

const NAVY = '#0A1128';
const NAVY_CARD = '#131F45';
const YELLOW = '#FFC81A';

const EASE = [0.16, 1, 0.3, 1];

const Reveal = ({ children, delay = 0, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 36 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-70px' }}
    transition={{ duration: 0.8, delay, ease: EASE }}
    className={className}
  >
    {children}
  </motion.div>
);

const MaskedLine = ({ children, delay = 0, className = '' }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: '112%' }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Stars = ({ size = 'w-5 h-5' }) => (
  <div className={`flex ${YELLOW ? '' : ''}`} style={{ color: YELLOW }}>
    {[...Array(5)].map((_, i) => (
      <Star key={i} className={`${size} fill-current`} />
    ))}
  </div>
);

const VSLPage = () => {
  const videoRef = useRef(null);
  const heroRef = useRef(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  // Smooth momentum scrolling
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, wheelMultiplier: 1 });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  // Calendly popup widget
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://assets.calendly.com/assets/external/widget.css';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);
  }, []);

  const openCalendly = () => {
    if (window.Calendly) {
      window.Calendly.initPopupWidget({ url: CALENDLY_URL });
    } else {
      window.open(CALENDLY_URL, '_blank');
    }
  };

  // Video autoplay when visible
  useEffect(() => {
    if (!videoRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isVideoPlaying) {
            videoRef.current?.play().catch(() => {});
          }
        });
      },
      { threshold: 0.4 }
    );
    observer.observe(videoRef.current);
    return () => observer.disconnect();
  }, [isVideoPlaying]);

  const handleVideoPlay = () => {
    if (videoRef.current) {
      videoRef.current.muted = false;
      videoRef.current.play();
      setIsVideoPlaying(true);
    }
  };

  // Subtle hero parallax
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const videoY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const heroTextY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <div className="min-h-screen bg-white font-body antialiased">
      {/* ===== ALERT BAR ===== */}
      <div className="py-3.5 px-4" style={{ background: YELLOW }} data-testid="alert-bar">
        <div className="container mx-auto max-w-5xl flex flex-col md:flex-row items-center justify-center gap-3 text-center">
          <p className="font-black text-sm md:text-base tracking-tight" style={{ color: NAVY }}>
            ⚡ ATENCIÓN: CALIFICA PARA REPARACIÓN DE CRÉDITO Y CONOCE CÓMO MEJORAR TU PERFIL
          </p>
          <motion.button
            data-testid="alert-cta-button"
            onClick={openCalendly}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.96 }}
            className="text-white px-6 py-2.5 rounded-full font-black text-sm whitespace-nowrap shadow-lg"
            style={{ background: NAVY }}
          >
            DA CLICK AQUÍ →
          </motion.button>
        </div>
      </div>

      {/* ===== HERO ===== */}
      <section ref={heroRef} className="relative overflow-hidden text-white px-4 pt-14 pb-20" style={{ background: `radial-gradient(120% 90% at 50% 0%, #16224D 0%, ${NAVY} 55%, #060B1D 100%)` }} data-testid="hero-section">
        {/* grain */}
        <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'2\'/%3E%3C/filter%3E%3Crect width=\'100%\' height=\'100%\' filter=\'url(%23n)\'/%3E%3C/svg%3E")' }} />

        <div className="container mx-auto max-w-5xl relative">
          <motion.div style={{ y: heroTextY }} className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE }}
              className="flex items-center justify-center gap-3 mb-8"
            >
              <Stars />
              <span className="font-bold text-base md:text-lg">4.9/5 • Reseñas de clientes</span>
            </motion.div>

            <h1 className="font-display font-black leading-[0.98] tracking-tight text-[13vw] sm:text-6xl md:text-7xl lg:text-[5.2rem] mb-6">
              <MaskedLine delay={0.15}>REPARACIÓN DE</MaskedLine>
              <MaskedLine delay={0.28}>CRÉDITO LEGAL</MaskedLine>
              <MaskedLine delay={0.41}>PARA LATINOS</MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6, ease: EASE }}
              className="font-display font-black text-2xl md:text-4xl tracking-tight mb-6"
              style={{ color: YELLOW }}
            >
              que están listos para dejar de pagar intereses abusivos
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
              className="text-lg md:text-2xl text-slate-300 font-medium max-w-3xl mx-auto mb-12"
            >
              🎯 Un proceso educativo y personalizado para avanzar con estrategia
            </motion.p>
          </motion.div>

          {/* Video */}
          <motion.div style={{ y: videoY }} className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 1, delay: 0.9, ease: EASE }}
              className="relative aspect-[9/16] max-w-[320px] sm:max-w-[380px] md:max-w-[420px] mx-auto rounded-2xl overflow-hidden border-4 shadow-2xl"
              style={{ borderColor: YELLOW, background: '#000' }}
              data-testid="hero-video"
            >
              <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover" controls playsInline preload="metadata" poster={VIDEO_POSTER} muted>
                <source src={VIDEO_URL} type="video/mp4" />
              </video>
              {!isVideoPlaying && (
                <>
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-black/80 to-transparent" />
                  <p className="pointer-events-none absolute bottom-4 left-5 right-5 text-white font-bold text-base md:text-xl drop-shadow-lg">
                    Mira cómo funciona nuestro proceso legal de reparación de crédito
                  </p>
                  <button
                    data-testid="video-play-button"
                    onClick={handleVideoPlay}
                    className="absolute inset-0 flex items-center justify-center group"
                  >
                  <motion.div
                    animate={{ scale: [1, 1.08, 1] }}
                    transition={{ repeat: Infinity, duration: 2.2, ease: 'easeInOut' }}
                    className="w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform"
                    style={{ background: YELLOW }}
                  >
                    <Play className="w-9 h-9 md:w-11 md:h-11 ml-1" style={{ color: NAVY }} fill="currentColor" />
                  </motion.div>
                  </button>
                </>
              )}
            </motion.div>

            <div className="flex flex-col sm:flex-row justify-between items-center gap-2 mt-5 text-sm md:text-base font-black tracking-wide">
              <span>17:42 MIN • MIRA HASTA EL FINAL</span>
              <span className="text-[#FB7185] flex items-center gap-2">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FB7185] opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#FB7185]"></span>
                </span>
                PRESENTACIÓN DISPONIBLE
              </span>
            </div>
          </motion.div>

          <Reveal delay={0.1} className="text-center mt-12">
            <motion.button
              data-testid="hero-cta-button"
              onClick={openCalendly}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              className="px-10 py-5 md:px-14 md:py-6 rounded-full font-black text-lg md:text-2xl tracking-tight inline-flex items-center gap-3 shadow-[0_20px_60px_-15px_rgba(255,200,26,0.5)]"
              style={{ background: YELLOW, color: NAVY }}
            >
              SÍ, QUIERO SOLICITAR MI CONSULTA
              <ArrowRight className="w-6 h-6" strokeWidth={3} />
            </motion.button>
            <p className="text-slate-400 text-sm md:text-base mt-5 font-medium">
              🔒 100% confidencial • Sin compromiso • Atención en español
            </p>
          </Reveal>
        </div>
      </section>

      {/* ===== MARQUEE ===== */}
      <div className="overflow-hidden py-4 border-y-4" style={{ background: YELLOW, borderColor: NAVY }} data-testid="marquee">
        <div className="animate-marquee flex whitespace-nowrap font-display font-black text-lg md:text-2xl tracking-tight" style={{ color: NAVY }}>
          {[0, 1].map((n) => (
            <span key={n} className="flex shrink-0">
              {['REPARACIÓN DE CRÉDITO LEGAL', 'SIN CPN NI IDENTIDADES FALSAS', 'PROCESO BAJO FCRA Y FDCPA', 'ATENCIÓN 100% EN ESPAÑOL', 'CONSULTA INICIAL GRATIS', 'OFICINA EN LAWRENCE, MA'].map((t, i) => (
                <span key={i} className="mx-6 flex items-center gap-6">
                  {t} <span className="text-xl">✦</span>
                </span>
              ))}
            </span>
          ))}
        </div>
      </div>

      {/* ===== PROBLEM ===== */}
      <section className="py-20 md:py-28 px-4 bg-white" data-testid="problem-section">
        <div className="container mx-auto max-w-5xl">
          <Reveal className="text-center mb-14">
            <div className="inline-block bg-[#FFF3C4] border border-[#F5E0A0] text-[#8A5A00] px-5 py-2 rounded-full text-sm font-black tracking-wide mb-6">
              🚨 LA VERDAD QUE NADIE TE DICE
            </div>
            <h2 className="font-display text-4xl md:text-6xl font-black tracking-tight mb-6" style={{ color: NAVY }}>
              ¿Por qué tantas familias aún no tienen acceso al crédito que merecen?
            </h2>
            <p className="text-lg md:text-2xl text-slate-500 font-medium max-w-3xl mx-auto">
              No siempre es falta de esfuerzo. Errores, cuentas negativas y una estrategia equivocada pueden cerrar puertas.
            </p>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-5 mb-8">
            {[
              { icon: '🪪', title: 'Usas ITIN o estás creando historial', text: 'Algunas instituciones tienen requisitos distintos. Conocer las opciones correctas evita solicitudes innecesarias.' },
              { icon: '📉', title: 'Errores y cuentas negativas', text: 'Colecciones, pagos tardíos, duplicados o información que no reconoces pueden afectar tu reporte.' },
              { icon: '💳', title: 'Score entre 500 y 650', text: 'Un perfil débil puede significar tasas más altas y menos opciones para financiar un vehículo o vivienda.' },
              { icon: '🏦', title: 'Requisitos difíciles', text: 'Sin preparación, los requisitos de ingreso, inicial y puntaje pueden sentirse como una barrera.' }
            ].map((item, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <motion.div
                  whileHover={{ y: -6 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 22 }}
                  className="bg-white border-2 border-slate-100 rounded-3xl p-7 md:p-9 shadow-[0_10px_40px_-18px_rgba(10,17,40,0.15)] h-full"
                >
                  <div className="text-4xl mb-4">{item.icon}</div>
                  <h3 className="font-display font-black text-xl md:text-2xl tracking-tight mb-3" style={{ color: NAVY }}>{item.title}</h3>
                  <p className="text-slate-500 text-base md:text-lg leading-relaxed">{item.text}</p>
                </motion.div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="bg-[#FFF1EB] border-2 border-[#F5CBB8] rounded-3xl p-6 md:p-8 flex gap-5 items-start">
              <div className="bg-[#F97316] text-white w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-2xl font-black shadow-lg">
                !
              </div>
              <p className="font-black text-lg md:text-2xl leading-snug tracking-tight" style={{ color: NAVY }}>
                El mal crédito puede costarte miles en intereses. Una revisión precisa te ayuda a separar errores disputables de información correcta que requiere estrategia.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== METHOD ===== */}
      <section className="py-20 md:py-28 px-4 bg-[#F5F6FA]" data-testid="method-section">
        <div className="container mx-auto max-w-5xl">
          <Reveal className="text-center mb-14">
            <div className="inline-block bg-[#FFF3C4] border border-[#F5E0A0] text-[#8A5A00] px-5 py-2 rounded-full text-sm font-black tracking-wide mb-6">
              ✅ LA SOLUCIÓN
            </div>
            <h2 className="font-display text-4xl md:text-6xl font-black tracking-tight mb-6" style={{ color: NAVY }}>
              El Método Restaurativo de Start Credit
            </h2>
            <p className="text-lg md:text-2xl text-slate-500 font-medium max-w-3xl mx-auto">
              Un proceso organizado basado en los derechos del consumidor bajo FCRA y FDCPA. No usamos CPN ni identidades falsas.
            </p>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-5">
            {[
              { day: 'Día 1–3', step: 'PASO 01', title: 'Análisis del reporte', text: 'Revisamos los tres burós para identificar errores, duplicados, cuentas negativas y oportunidades de optimización.' },
              { day: 'Día 4–20', step: 'PASO 02', title: 'Disputas fundamentadas', text: 'Preparamos disputas sobre información inexacta, incompleta o no verificable conforme a la ley aplicable.' },
              { day: 'Día 21–45', step: 'PASO 03', title: 'Estrategia positiva', text: 'Trabajamos utilización, antigüedad, pagos y combinación de cuentas según las necesidades del caso.' },
              { day: 'Seguimiento', step: 'PASO 04', title: 'Resultados y próximos pasos', text: 'Comparamos las actualizaciones, explicamos los cambios y ajustamos la estrategia cuando sea necesario.' }
            ].map((item, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <motion.div
                  whileHover={{ y: -6 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 22 }}
                  className="bg-white rounded-3xl p-7 md:p-9 shadow-[0_10px_40px_-18px_rgba(10,17,40,0.18)] h-full"
                >
                  <div className="text-[#2563EB] font-black text-lg mb-1">{item.day}</div>
                  <div className="text-slate-400 font-black text-sm tracking-[0.2em] mb-4">{item.step}</div>
                  <h3 className="font-display font-black text-xl md:text-2xl tracking-tight mb-3" style={{ color: NAVY }}>{item.title}</h3>
                  <p className="text-slate-500 text-base md:text-lg leading-relaxed mb-6">{item.text}</p>
                  <span className="inline-block bg-[#D9F5E3] text-[#166534] px-4 py-1.5 rounded-full text-sm font-black">
                    Incluido
                  </span>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== COST ===== */}
      <section className="py-20 md:py-28 px-4 bg-white" data-testid="cost-section">
        <div className="container mx-auto max-w-4xl">
          <Reveal className="text-center mb-12">
            <h2 className="font-display text-4xl md:text-6xl font-black tracking-tight mb-6" style={{ color: NAVY }}>
              Lo que puede costar el mal crédito
            </h2>
            <p className="text-lg md:text-2xl text-slate-500 font-medium max-w-2xl mx-auto">
              Ejemplo educativo: la tasa real depende del prestamista, plazo, ingreso y perfil completo.
            </p>
          </Reveal>

          <Reveal>
            <div className="rounded-3xl overflow-hidden border-2 border-slate-100 shadow-2xl">
              <table className="w-full">
                <thead>
                  <tr className="text-white">
                    <th className="p-4 md:p-6 text-left font-black text-sm md:text-lg" style={{ background: NAVY }}>Tipo de préstamo</th>
                    <th className="p-4 md:p-6 text-left font-black text-sm md:text-lg bg-[#8B1E1E]">Perfil débil</th>
                    <th className="p-4 md:p-6 text-left font-black text-sm md:text-lg bg-[#166534] hidden sm:table-cell">Perfil más sólido</th>
                  </tr>
                </thead>
                <tbody className="text-sm md:text-lg">
                  {[
                    { icon: '🚗', name: 'Auto ($35,000)', weak: 'APR potencialmente mayor', strong: 'Más opciones y menor costo mensual posible' },
                    { icon: '🏠', name: 'Casa ($350,000)', weak: 'Pago e interés más alto', strong: 'Ahorro potencial de miles a largo plazo' },
                    { icon: '💳', name: 'Préstamo personal', weak: 'Condiciones limitadas', strong: 'Mejores tasas y límites potenciales' }
                  ].map((row, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="p-4 md:p-6 font-black" style={{ color: NAVY }}>{row.icon} {row.name}</td>
                      <td className="p-4 md:p-6 text-slate-500">{row.weak}</td>
                      <td className="p-4 md:p-6 hidden sm:table-cell">
                        <span className="text-slate-500">{row.strong}</span>
                        <div className="text-[#16A34A] font-bold text-sm mt-1">✓ Mejores condiciones</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="bg-[#D9F5E3] border-2 border-[#A7E5C0] rounded-3xl p-6 md:p-8 mt-8 text-center font-black text-lg md:text-2xl tracking-tight text-[#14532D]">
              💰 Mejorar el perfil no garantiza una aprobación, pero puede ampliar tus opciones y reducir el costo del crédito.
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="py-20 md:py-28 px-4 text-white" style={{ background: `radial-gradient(120% 80% at 50% 0%, #16224D 0%, ${NAVY} 60%, #060B1D 100%)` }} data-testid="testimonials-section">
        <div className="container mx-auto max-w-6xl">
          <Reveal className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-white/5 border border-white/15 px-5 py-2 rounded-full text-sm font-black tracking-wide mb-6" style={{ color: YELLOW }}>
              ⭐ HISTORIAS DE CLIENTES
            </div>
            <h2 className="font-display text-4xl md:text-6xl font-black tracking-tight mb-6">
              Personas que decidieron trabajar su crédito
            </h2>
            <p className="text-slate-300 text-lg md:text-xl max-w-2xl mx-auto font-medium">
              Los resultados dependen de cada reporte. Testimonios de clientes con resultados verificados.
            </p>
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { photo: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?crop=entropy&cs=srgb&fm=jpg&w=200&h=200&fit=crop', name: 'María G.', city: 'Lawrence, MA', before: '540', after: '698', badge: '+158 PUNTOS EN 4 MESES', quote: 'Increíble servicio! Eliminaron 3 colecciones que ni siquiera sabía que tenía. Ahora puedo aplicar para mi primer auto sin miedo.' },
              { photo: 'https://images.unsplash.com/photo-1656399910161-8efafd973a29?crop=entropy&cs=srgb&fm=jpg&w=200&h=200&fit=crop', name: 'Carlos R.', city: 'Boston, MA', before: '485', after: '715', badge: '+230 PUNTOS EN 6 MESES', quote: 'Estaba pagando intereses altísimos. Rhomy y su equipo me ayudaron a limpiar mi reporte y ahora tengo una tarjeta con mejor límite. Muy profesionales!' },
              { photo: 'https://images.unsplash.com/photo-1631377307692-36a9b6ae3ef6?crop=entropy&cs=srgb&fm=jpg&w=200&h=200&fit=crop', name: 'Ana L.', city: 'Lowell, MA', before: '562', after: '742', badge: '+180 PUNTOS EN 5 MESES', quote: 'Me explicaron todo paso a paso. Eliminaron pagos tardíos que afectaban mi score. Ahora estoy lista para comprar mi casa!' },
              { photo: 'https://images.unsplash.com/photo-1705645930353-0e335311ef20?crop=entropy&cs=srgb&fm=jpg&w=200&h=200&fit=crop', name: 'José M.', city: 'Methuen, MA', before: '510', after: '680', badge: '+170 PUNTOS EN 4 MESES', quote: 'Pensé que nunca podría mejorar mi crédito. Start Credit me demostró lo contrario. Proceso legal y resultados reales. 100% recomendados.' },
              { photo: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&w=200&h=200&fit=crop', name: 'Rosa P.', city: 'Haverhill, MA', before: '595', after: '760', badge: '+165 PUNTOS EN 5 MESES', quote: 'Servicio excepcional! Me ayudaron con ITIN y ahora tengo mejor crédito que muchos con SSN. Gracias por su paciencia y profesionalismo.' },
              { photo: 'https://images.unsplash.com/photo-1718209881007-c0ecdfc00f9d?crop=entropy&cs=srgb&fm=jpg&w=200&h=200&fit=crop', name: 'Miguel S.', city: 'Salem, MA', before: '528', after: '705', badge: '+177 PUNTOS EN 6 MESES', quote: 'Excelente atención en español. Rhomy me guió en cada paso y ahora tengo el crédito que necesitaba para mi negocio. Muy agradecido!' }
            ].map((t, i) => (
              <Reveal key={i} delay={(i % 3) * 0.08}>
                <motion.div
                  whileHover={{ y: -6, borderColor: 'rgba(255,200,26,0.6)' }}
                  transition={{ type: 'spring', stiffness: 300, damping: 22 }}
                  className="rounded-3xl p-6 md:p-7 h-full border border-white/10"
                  style={{ background: NAVY_CARD }}
                  data-testid={`testimonial-card-${i}`}
                >
                  <div className="flex items-center gap-4 mb-5">
                    <img src={t.photo} alt={t.name} className="w-14 h-14 rounded-full object-cover border-2" style={{ borderColor: YELLOW }} loading="lazy" />
                    <div>
                      <p className="font-black text-lg">{t.name}</p>
                      <p className="text-sm text-slate-400">{t.city} • Caso individual</p>
                    </div>
                  </div>
                  <Stars size="w-4 h-4" />
                  <div className="rounded-xl p-4 my-4" style={{ background: '#060B1D' }}>
                    <div className="flex justify-between items-center text-sm">
                      <div>
                        <span className="text-slate-400">Antes:</span>
                        <div className="text-[#FB7185] font-black text-2xl">{t.before}</div>
                      </div>
                      <ArrowRight className="w-5 h-5" style={{ color: YELLOW }} />
                      <div>
                        <span className="text-slate-400">Después:</span>
                        <div className="font-black text-2xl" style={{ color: YELLOW }}>{t.after}</div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-[#166534] text-white text-xs px-3 py-1.5 rounded-full inline-block mb-4 font-black tracking-wide">
                    {t.badge}
                  </div>
                  <p className="text-slate-300 text-sm md:text-base italic leading-relaxed">"{t.quote}"</p>
                </motion.div>
              </Reveal>
            ))}
          </div>

          <p className="text-center text-slate-500 text-sm mt-10 max-w-3xl mx-auto">
            * Los resultados varían según el historial y las circunstancias de cada consumidor. No se garantiza un aumento específico del puntaje.
          </p>
        </div>
      </section>

      {/* ===== IDEAL CLIENT (kept) ===== */}
      <section className="py-20 md:py-28 px-4 bg-white" data-testid="ideal-client-section">
        <div className="container mx-auto max-w-5xl">
          <Reveal className="text-center mb-14">
            <div className="inline-block bg-[#FFF3C4] border border-[#F5E0A0] text-[#8A5A00] px-5 py-2 rounded-full text-sm font-black tracking-wide mb-6">
              ¿ESTE PROCESO ES PARA TI?
            </div>
            <h2 className="font-display text-4xl md:text-6xl font-black tracking-tight" style={{ color: NAVY }}>
              Start Credit es ideal para quienes están listos para avanzar
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-5 mb-10">
            <Reveal>
              <div className="bg-[#F5F6FA] rounded-3xl p-7 md:p-9 h-full">
                {[
                  'Tu puntaje no refleja el esfuerzo que haces para manejar tus finanzas.',
                  'Tienes ITIN o SSN y quieres conocer tus opciones.',
                  'Quieres prepararte para comprar un vehículo o una vivienda.',
                  'Tienes colecciones, pagos tardíos u otras cuentas negativas.',
                  'Buscas un proceso legal, educativo y organizado.',
                  'Estás dispuesto a mantener activo tu monitoreo de crédito.'
                ].map((item, i) => (
                  <div key={i} className="flex gap-3 mb-4 last:mb-0">
                    <CheckCircle2 className="w-6 h-6 text-[#16A34A] flex-shrink-0 mt-0.5" />
                    <p className="text-slate-600 text-base md:text-lg">{item}</p>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="bg-[#FFF1F2] border-2 border-[#FDA4AF] rounded-3xl p-7 md:p-9 h-full">
                <h3 className="text-[#9F1239] font-display font-black text-2xl md:text-3xl tracking-tight mb-4">NO ES PARA TI SI:</h3>
                <p className="text-slate-600 text-base md:text-lg leading-relaxed">
                  Buscas CPN, un SSN falso, una identidad nueva o un borrado mágico en 24 horas. Start Credit trabaja únicamente con procesos legales.
                </p>
              </div>
            </Reveal>
          </div>

          <Reveal>
            <div className="max-w-3xl mx-auto bg-white border-2 border-slate-100 rounded-3xl p-7 md:p-10 shadow-2xl">
              <div className="inline-block bg-[#FFF3C4] border border-[#F5E0A0] text-[#8A5A00] px-4 py-1.5 rounded-full text-xs font-black tracking-wide mb-5">
                LO QUE INCLUYE
              </div>
              <h3 className="font-display text-2xl md:text-4xl font-black tracking-tight mb-7" style={{ color: NAVY }}>
                Tu llamada estratégica gratuita
              </h3>
              <ul className="space-y-4 mb-7">
                {[
                  'Revisión inicial de tus objetivos y dificultades',
                  'Orientación sobre los tres burós y próximos pasos',
                  'Explicación del proceso de disputa aplicable',
                  'Plan de acción según tu situación',
                  'Explicación de servicios y precios'
                ].map((item, i) => (
                  <li key={i} className="flex gap-3 border-b border-slate-100 pb-4 text-base md:text-lg">
                    <span className="text-[#16A34A] font-black">✓</span>
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="bg-[#EFF6FF] border border-[#93C5FD] rounded-2xl p-5 text-[#1E3A8A] font-bold text-base md:text-lg">
                Si nuestro servicio no encaja con tu situación, te lo diremos claramente. Sin presión y sin venderte falsas promesas.
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== GUARANTEE (kept) ===== */}
      <section className="py-20 md:py-28 px-4 bg-[#F5F6FA]" data-testid="guarantee-section">
        <div className="container mx-auto max-w-4xl">
          <Reveal>
            <div className="border-4 border-[#16A34A] rounded-3xl bg-white p-8 md:p-12 text-center shadow-2xl">
              <Shield className="w-16 h-16 text-[#16A34A] mx-auto mb-5" />
              <h2 className="font-display text-3xl md:text-5xl font-black tracking-tight mb-5" style={{ color: NAVY }}>
                GARANTÍA RESPALDADA POR CONTRATO
              </h2>
              <p className="text-lg md:text-xl text-slate-600 mb-5 max-w-2xl mx-auto">
                Si después de 45 días no observas resultados conforme a las condiciones establecidas en el contrato, recibirás la devolución correspondiente y continuaremos trabajando tu proceso sin costo adicional.
              </p>
              <p className="text-sm text-slate-500 max-w-2xl mx-auto">
                La garantía no representa una promesa de un puntaje específico, aprobación bancaria ni eliminación de información correcta y verificable. Aplican los términos del contrato firmado.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== BOOKING (calendar in a button) ===== */}
      <section id="agendar" className="py-20 md:py-28 px-4 text-white" style={{ background: `radial-gradient(120% 80% at 50% 0%, #16224D 0%, ${NAVY} 60%, #060B1D 100%)` }} data-testid="booking-section">
        <div className="container mx-auto max-w-3xl">
          <Reveal className="text-center mb-10">
            <h2 className="font-display text-4xl md:text-6xl font-black tracking-tight mb-6">
              Agenda tu llamada estratégica
            </h2>
            <div className="flex flex-wrap justify-center gap-3 mb-10">
              {['📱 Teléfono o videollamada', '🌎 Español', '🔒 Confidencial'].map((chip, i) => (
                <span key={i} className="bg-white/5 border border-white/15 px-5 py-2.5 rounded-full text-sm md:text-base font-bold">
                  {chip}
                </span>
              ))}
            </div>
          </Reveal>

          <Reveal>
            <div className="rounded-3xl p-8 md:p-12 text-center border border-white/10" style={{ background: NAVY_CARD }}>
              <motion.button
                data-testid="booking-open-button"
                onClick={openCalendly}
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.97 }}
                className="w-full px-8 py-6 rounded-full font-black text-xl md:text-2xl tracking-tight inline-flex items-center justify-center gap-3 shadow-[0_20px_60px_-15px_rgba(255,200,26,0.5)]"
                style={{ background: YELLOW, color: NAVY }}
              >
                📅 AGENDAR MI CONSULTA
                <ArrowRight className="w-6 h-6" strokeWidth={3} />
              </motion.button>
              <p className="text-slate-300 text-base md:text-lg mt-6">
                Próximo paso: <span className="text-[#4ADE80] font-black">elige fecha y hora</span>
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== ÚLTIMO PASO ===== */}
      <section className="pb-20 md:pb-28 px-4 text-white" style={{ background: '#060B1D' }} data-testid="final-cta-section">
        <div className="container mx-auto max-w-3xl">
          <Reveal>
            <div className="rounded-3xl p-8 md:p-14 text-center border border-white/10" style={{ background: NAVY_CARD }}>
              <div className="inline-block bg-white/5 border border-white/15 px-5 py-2 rounded-full text-sm font-black tracking-wide mb-7">
                🚀 ÚLTIMO PASO
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-black tracking-tight mb-6">
                ¿Quieres saber si nuestro proceso es para ti?
              </h2>
              <p className="text-slate-300 text-lg md:text-xl mb-9 max-w-xl mx-auto">
                Revisamos tu situación y te explicamos los próximos pasos. Sin presión y sin letras pequeñas.
              </p>
              <motion.button
                data-testid="final-cta-button"
                onClick={openCalendly}
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.97 }}
                className="w-full px-8 py-6 rounded-full font-black text-xl md:text-2xl tracking-tight inline-flex items-center justify-center gap-3 shadow-[0_20px_60px_-15px_rgba(255,200,26,0.5)] mb-9"
                style={{ background: YELLOW, color: NAVY }}
              >
                SÍ, QUIERO SOLICITAR MI CONSULTA
                <ArrowRight className="w-6 h-6" strokeWidth={3} />
              </motion.button>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                {[
                  { big: '15 min', small: 'Llamada' },
                  { big: '100%', small: 'Confidencial' },
                  { big: '$0', small: 'Consulta inicial' }
                ].map((s, i) => (
                  <div key={i} className="rounded-2xl p-6" style={{ background: '#060B1D' }}>
                    <div className="font-display font-black text-3xl md:text-4xl mb-1" style={{ color: YELLOW }}>{s.big}</div>
                    <div className="text-slate-300">{s.small}</div>
                  </div>
                ))}
              </div>

              <p className="text-slate-400 text-sm md:text-base">
                🔒 No compartimos tu información • Proceso conforme a FCRA y FDCPA
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="text-slate-300 py-14 px-4" style={{ background: '#04070F' }} data-testid="footer">
        <div className="container mx-auto max-w-5xl">
          <div className="flex items-center gap-4 mb-5">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center font-display font-black text-3xl" style={{ background: YELLOW, color: NAVY }}>
              S
            </div>
            <span className="text-white font-display font-black text-2xl tracking-tight">Start Credit LLC</span>
          </div>
          <p className="text-lg mb-6 max-w-2xl">
            Reparación de crédito y educación financiera para consumidores. Atención presencial en Lawrence, Massachusetts.
          </p>
          <div className="flex gap-3 flex-wrap mb-10">
            <span className="border border-slate-700 px-4 py-2 rounded-full text-sm font-bold">🇺🇸 SERVICIO EN EE.UU.</span>
            <span className="border border-slate-700 px-4 py-2 rounded-full text-sm font-bold">✓ ENFOQUE FCRA</span>
          </div>

          <h3 className="text-white font-black text-xl mb-4">Aviso legal</h3>
          <p className="text-slate-400 leading-relaxed mb-10 max-w-4xl">
            Start Credit LLC no es una firma de abogados y no ofrece asesoría legal. El servicio se basa en derechos del consumidor bajo FCRA, FDCPA y CROA. No garantizamos un puntaje, una aprobación ni la eliminación de información precisa y verificable. Los resultados y tiempos varían según el caso. Al enviar una solicitud aceptas que Start Credit se comunique contigo para atenderla; puedes pedir que no te contacten nuevamente.
          </p>

          <div className="border-t border-slate-800 pt-7 text-sm text-slate-500">
            © 2026 Start Credit LLC • Lawrence, Massachusetts • Todos los derechos reservados
          </div>
        </div>
      </footer>
    </div>
  );
};

export default VSLPage;
